/// <reference types="@types/google.maps" />
import {type AutocompleteState, createAutocomplete} from '@algolia/autocomplete-core'
import {announce} from '@react-aria/live-announcer'
import {type BaseSyntheticEvent, type Dispatch, type KeyboardEvent, type MouseEvent} from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import locationSvg from '@/assets/food/icons/location.svg'
import CloseSvg from '@/assets/icons/close.svg?react'
import MicrophoneSvg from '@/assets/icons/microphone.svg?react'
import MuteSvg from '@/assets/icons/mute.svg?react'
import useVoiceToText from '@/hooks/useVoiceToText'
import type {
  AddressSuggestionItem,
  UserAddressComponentProps,
} from '@/lib/food-grubhub/foodGrubhubModel'
import {useSetGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {FoodGrubhubRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import debouncePromise from '@/utils/debouncePromise'

// import useSetSearch from '@/utils/router/useSetSearch'
import style from './FoodGrubhubInput.module.scss'

// @ts-expect-error -- workaround
const debounced: <T extends unknown[]>(items: T) => Promise<T> = debouncePromise(
  async items => Promise.resolve(items),
  300,
)

export interface SearchInputControl {
  setSearchInput: Dispatch<string>
  submit: () => void
  focus: () => void
}

interface Props {
  control?: RefObject<SearchInputControl | null>
  placeholderText?: string
  variant?: 'navbar' | 'main'
  setResetInput?: (value: boolean) => void
}

interface BaseItem extends Record<string, unknown> {
  content: AddressSuggestionItem
}

const StoreItem = memo<
  {
    item: BaseItem
    query: string
    isActive: boolean
  } & React.LiHTMLAttributes<HTMLLIElement>
>(function StoreItem({item, isActive, ...props}) {
  return (
    <li className={clsx(style.Item, isActive && style.IsActive)} {...props}>
      <div className={style.ItemLink}>
        <div className={style.ItemContent}>
          <img className={style.ItemDecor} src={locationSvg} alt='' />
          <div className={style.ItemLabel}>{item.content.description}</div>
        </div>
      </div>
    </li>
  )
})

export default memo<Props>(function FoodGrubhubInput({
  control,
  placeholderText,
  variant,
  setResetInput,
}) {
  const navigate = useNavigate()
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const setStorageUserLocation = useSetGrubhubUserAddress()

  const {placesService, placePredictions, getPlacePredictions} = usePlacesService({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  })

  // const {lng, lat, address} = FoodGrubhubRoute.useSearch()

  // const setLongitude = useSetSearch(FoodGrubhubRoute.id, 'lng')
  // const setLatitude = useSetSearch(FoodGrubhubRoute.id, 'lat')
  // const setAddress = useSetSearch(FoodGrubhubRoute.id, 'address')

  const [autocompleteState, setAutocompleteState] = useState<AutocompleteState<BaseItem>>({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: '',
    activeItemId: null,
    status: 'idle',
  })

  const getShortNameByType = (components: UserAddressComponentProps[], type: string) => {
    const component = components.find(comp => comp.types?.includes(type))
    return component ? component.short_name : null
  }

  const handleSelect = useCallback(
    (placeId: string) => {
      if (!placesService) return

      placesService.getDetails({placeId}, (placeDetails, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && placeDetails) {
          const lat = Number(placeDetails.geometry?.location?.lat().toFixed(7))
          const lng = Number(placeDetails.geometry?.location?.lng().toFixed(7))

          // setLongitude(lng)
          // setLatitude(lat)
          // setAddress(placeDetails.formatted_address)
          setStorageUserLocation({
            latitude: lat || 0,
            longitude: lng || 0,
            address: placeDetails.formatted_address ?? '',
            address_detail: {
              street_number:
                getShortNameByType(placeDetails.address_components ?? [], 'street_number') ?? '',
              route: getShortNameByType(placeDetails.address_components ?? [], 'route') ?? '',
              locality: getShortNameByType(placeDetails.address_components ?? [], 'locality') ?? '',
              administrative_area_level_1:
                getShortNameByType(
                  placeDetails.address_components ?? [],
                  'administrative_area_level_1',
                ) ?? '',
              country: getShortNameByType(placeDetails.address_components ?? [], 'country') ?? '',
              postal_code:
                getShortNameByType(placeDetails.address_components ?? [], 'postal_code') ?? '',
            },
          })

          // setInputValue(placeDetails.formatted_address ?? '')
          getPlacePredictions({input: ''})
        } else {
          console.error('Failed to fetch details:', status)
        }
      })

      setResetInput?.(false)
    },
    [
      placesService,
      // setLongitude,
      // setLatitude,
      // setAddress,
      setStorageUserLocation,
      getPlacePredictions,
      setResetInput,
    ],
  )

  const handleSearchClick = useCallback(
    (inputValue: string) => {
      if (!inputValue) return

      const geocoder = new google.maps.Geocoder()

      geocoder.geocode(
        {address: inputValue, componentRestrictions: {country: 'US'}},
        (results, status) => {
          if (results && results.length > 0) {
            const result = results[0]
            const lat = result?.geometry.location.lat()
            const lng = result?.geometry.location.lng()
            // setLongitude(lng)
            // setLatitude(lat)
            // setAddress(result?.formatted_address)
            setStorageUserLocation({
              latitude: lat ?? 0,
              longitude: lng ?? 0,
              address: result?.formatted_address ?? '',
              address_detail: {
                street_number:
                  getShortNameByType(result?.address_components ?? [], 'street_number') ?? '',
                route: getShortNameByType(result?.address_components ?? [], 'route') ?? '',
                locality: getShortNameByType(result?.address_components ?? [], 'locality') ?? '',
                administrative_area_level_1:
                  getShortNameByType(
                    result?.address_components ?? [],
                    'administrative_area_level_1',
                  ) ?? '',
                country: getShortNameByType(result?.address_components ?? [], 'country') ?? '',
                postal_code:
                  getShortNameByType(result?.address_components ?? [], 'postal_code') ?? '',
              },
            })

            // setInputValue(result?.formatted_address ?? '')
          } else {
            console.error('Geocoding failed:', status)
          }
        },
      )

      setResetInput?.(false)
    },
    [
      // setLongitude,
      // setLatitude,
      // setAddress,
      setStorageUserLocation,
      setResetInput,
    ],
  )

  const getHref = useCallback(() => {
    return router.buildLocation({
      to: FoodGrubhubRoute.fullPath,
    }).href
  }, [router])

  const latestGetPlacePredictions = useStableCallback(getPlacePredictions)
  const latestPlacePredictions = useLatest(placePredictions)
  const latestHandleSelect = useStableCallback(handleSelect)
  const latestHandleSearchClick = useStableCallback(handleSearchClick)

  const autocomplete = useMemo(
    () =>
      createAutocomplete<BaseItem, BaseSyntheticEvent, MouseEvent, KeyboardEvent>({
        // debug: true, // Keep it open when development
        placeholder: placeholderText ?? 'Enter your delivery address',
        initialState: {
          // query: '',
        },
        openOnFocus: true,
        onStateChange(params) {
          setAutocompleteState(params.state)
        },
        navigator: {
          navigate({itemUrl}) {
            void navigate({
              to: itemUrl,
              resetScroll: true,
            })
          },
          navigateNewTab({itemUrl}) {
            const windowReference = window.open(itemUrl, '_blank', 'noopener')

            if (windowReference) {
              windowReference.focus()
            }
          },
          navigateNewWindow({itemUrl}) {
            window.open(itemUrl, '_blank', 'noopener')
          },
        },
        async getSources() {
          return debounced([
            {
              sourceId: 'suggestions',
              getItemInputValue(params) {
                return params.item.content.description
              },
              getItems(params) {
                if (!params.query.trim()) return []
                try {
                  latestGetPlacePredictions({
                    input: params.query,
                    componentRestrictions: {country: 'us'},
                  })
                  return latestPlacePredictions.current.map(item => ({content: item}))
                } catch {
                  return []
                }
              },
              getItemUrl() {
                return getHref()
              },
              onSelect(params) {
                params.setIsOpen(false)
                inputRef.current?.blur()
                // handleSelect(params.item.content.place_id)
                latestHandleSelect(params.item.content.place_id)
                if (params.itemUrl) {
                  params.navigator.navigate({
                    itemUrl: params.itemUrl,
                    item: params.item,
                    state: params.state,
                  })
                }
                setResetInput?.(false)
              },
              onResolve: params => {
                if (!Array.isArray(params.items)) {
                  return
                }

                const totalSuggestions = params.items.reduce(
                  (total: number, item) => total + (Array.isArray(item) ? item.length : 1),
                  0,
                )

                announce(
                  `Found ${totalSuggestions} suggestions for "${params.state.query}"`,
                  'polite',
                )

                // await cancelAllPreloadResults(queryClient)

                params.items.forEach(() => {
                  //
                })
              },
              onActive() {
                //
              },
            },
          ])
        },
        onSubmit(params) {
          //   handleSearchClick(params.state.query)
          latestHandleSearchClick(params.state.query)
        },
      }),
    [navigate, placeholderText, getHref, setResetInput],
  )

  const latestAutocomplete = useLatest(autocomplete)

  const {getEnvironmentProps} = autocomplete

  useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined
    }

    // TODO: check this error
    // eslint-disable-next-line @typescript-eslint/unbound-method -- dont know how to fix it
    const {onTouchStart, onTouchMove, onMouseDown} = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    })

    const abortController = new AbortController()

    window.addEventListener('mousedown', onMouseDown, {signal: abortController.signal})
    window.addEventListener('touchstart', onTouchStart, {signal: abortController.signal})
    window.addEventListener('touchmove', onTouchMove, {signal: abortController.signal})

    return () => {
      abortController.abort()
    }
  }, [getEnvironmentProps, autocompleteState.isOpen])

  const {startListening, stopListening, transcript, reset, isSpeechRecognitionAvailable} =
    useVoiceToText()
  const latestSpeechRecognitionIsAvailable = useLatest(isSpeechRecognitionAvailable)
  const latestStartListening = useStableCallback(startListening)
  const latestStopListening = useStableCallback(stopListening)
  const latestReset = useStableCallback(reset)

  const [isListening, setIsListening] = useState(false)
  const latestIsListening = useLatest(isListening)

  const onVoiceModePressed = useCallback<MouseEventHandler<HTMLButtonElement>>(e => {
    e.preventDefault()
    if (!latestSpeechRecognitionIsAvailable.current) return
    if (latestIsListening.current) {
      latestOnVoiceModeEnd()
    } else {
      latestStartListening()
      setIsListening(true)
      announce('Listening...', 'polite')
    }
  }, [])

  const onVoiceModeEnd = useCallback(() => {
    if (!latestIsListening.current) return
    if (!latestSpeechRecognitionIsAvailable.current) return
    latestStopListening()
    latestReset()
    setIsListening(false)
    inputRef.current?.focus()
  }, [])
  const latestOnVoiceModeEnd = useStableCallback(onVoiceModeEnd)

  useEffect(() => {
    if (!transcript) return
    latestAutocomplete.current.setQuery(transcript)
    void latestAutocomplete.current.refresh()
  }, [transcript])

  useImperativeHandle(control, () => {
    return {
      setSearchInput: latestAutocomplete.current.setQuery,
      submit: () => {
        formRef.current?.requestSubmit()
      },
      focus: () => {
        inputRef.current?.focus()
      },
    }
  }, [])

  return (
    <div
      className={clsx(
        style.AutoCompleteContainer,
        variant === 'main' && style.AutoCompleteContainerMain,
      )}
      {...autocomplete.getRootProps({role: 'none'})}
    >
      <form
        ref={formRef}
        className={style.Form}
        // eslint-disable-next-line react-hooks/refs -- ignore
        {...autocomplete.getFormProps({inputElement: inputRef.current})}
      >
        <div className={style.InputWrapper}>
          <input
            className={style.Input}
            ref={inputRef}
            aria-label='Enter your delivery address'
            // eslint-disable-next-line react-hooks/refs -- ignore
            {...autocomplete.getInputProps({inputElement: inputRef.current, role: 'combobox'})}
          />
        </div>
        <div className={style.InputWrapperPrefix} />
        <div className={style.InputWrapperSuffix}>
          {isSpeechRecognitionAvailable && (
            <button
              type='button'
              onClick={onVoiceModePressed}
              className={clsx(style.ClearButton)}
              aria-label={isListening ? 'Stop dictation mode' : 'Start dictation mode'}
              onBlur={onVoiceModeEnd}
            >
              {isListening ? <MuteSvg /> : <MicrophoneSvg />}
            </button>
          )}
          <button
            className={clsx(
              style.ClearButton,
              variant === 'main' && style.ClearButton,
              autocompleteState.query.length === 0 && style.IsHidden,
            )}
            aria-label='Clear search input'
            type='reset'
            aria-disabled={autocompleteState.query.length === 0}
            tabIndex={autocompleteState.query.length === 0 ? -1 : 0}
            onClick={() => {
              reset()
            }}
          >
            <CloseSvg aria-hidden='true' />
          </button>
          <label className={style.Label} {...autocomplete.getLabelProps({})}>
            <button className={style.SubmitButton} type='submit' aria-label='Search'>
              <img
                src={locationSvg}
                alt=''
                className={clsx(style.SubmitIcon, variant === 'main' && style.SubmitIconMain)}
              />
            </button>
          </label>
        </div>
      </form>

      {/* {placePredictions.length > 0 && (
        <div
          ref={panelRef}
          className={clsx(style.Panel, variant === 'main' && style.Main)}
          {...autocomplete.getPanelProps({})}
        >
          <div className={style.PanelLayout}>
            {autocompleteState.collections.map((collection, index) => {
              const {source} = collection

              return (
                <section key={`source-${index}`} className={style.Source}>
                  {autocompleteState.query && (
                    <VisuallyHidden>{`Found ${placePredictions.length} suggestions for "${autocompleteState.query}"`}</VisuallyHidden>
                  )}
                  {placePredictions.length > 0 && (
                    <ul className={style.List} {...autocomplete.getListProps()}>
                      {placePredictions.map((item, index) => {
                        const rest = autocomplete.getItemProps({
                          item: {content: item},
                          source,
                        })
                        return (
                          <StoreItem
                            key={index}
                            isActive={autocompleteState.activeItemId === index}
                            query={autocompleteState.query}
                            item={{content: item}}
                            {...rest}
                          />
                        )
                      })}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )} */}
      {autocompleteState.isOpen && (
        <div
          ref={panelRef}
          className={clsx(style.Panel, variant === 'main' && style.Main)}
          {...autocomplete.getPanelProps({})}
        >
          <div className={style.PanelLayout}>
            {autocompleteState.collections.map((collection, index) => {
              const {source, items} = collection

              return (
                <section key={`source-${index}`} className={style.Source}>
                  {autocompleteState.query && (
                    <VisuallyHidden>{`Found ${items.length} suggestions for "${autocompleteState.query}"`}</VisuallyHidden>
                  )}
                  {items.length > 0 && (
                    <ul className={style.List} {...autocomplete.getListProps()}>
                      {items.map((item, index) => {
                        const rest = autocomplete.getItemProps({
                          item,
                          source,
                        })
                        return (
                          <StoreItem
                            key={index}
                            isActive={autocompleteState.activeItemId === index}
                            query={autocompleteState.query}
                            item={item}
                            {...rest}
                          />
                        )
                      })}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})
